import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Typography,
  Space,
  Divider,
  Button,
  Form,
  Input,
  Select,
  Table,
  Modal,
  Grid
} from "antd";
import {
  PlusOutlined,
  GlobalOutlined,
  UserAddOutlined,
  SearchOutlined
} from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import { Severty, ShowToast } from "../../helper/toast";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const AirlineModule = () => {
  const screens = useBreakpoint();
  const { request } = useRequest();
  
  // State Management
  const [loading, setLoading] = useState(false);
  const [airlines, setAirlines] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const [addForm] = Form.useForm();

  // Load appropriate data on mount
  useEffect(() => {
    loadAirlines();
  }, []);

  // Admin Flow: Fetch existing airlines
  const loadAirlines = () => {
    setLoading(true);
    request({
      url: "/airline/users",
      method: "GET",
      onSuccess: (res) => {
        if (res?.success) {
          setAirlines(res.users || []);
        } else {
          setAirlines([]);
        }
        setLoading(false);
      },
      onError: () => {
        setAirlines([]);
        setLoading(false);
      },
    });
  };

  // Create airline user
  const handleCreateAirlineUser = (values) => {
    setAddLoading(true);
    request({
      url: "/airline/create-user",
      method: "POST",
      data: {
        name: values.name,
        email: values.email,
        password: values.password,
        status: values.status !== undefined ? values.status : 1,
      },
      onSuccess: (res) => {
        setAddLoading(false);
        if (res?.success) {
          ShowToast(res.message || "Airline user created successfully!", Severty.SUCCESS);
          setAddModalVisible(false);
          addForm.resetFields();
          loadAirlines();
        } else {
          ShowToast(res?.message || "Failed to create airline user", Severty.ERROR);
        }
      },
      onError: (err) => {
        setAddLoading(false);
        const msg = err?.response?.data?.message || "Error creating airline user";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  // Update airline status
  const handleUpdateStatus = (record) => {
    const id = record._id || record.id;
    setLoading(true);
    request({
      url: `/airline/update-status/${id}`,
      method: "POST",
      onSuccess: (res) => {
        setLoading(false);
        if (res?.success) {
          ShowToast(res.message || "Status updated successfully", Severty.SUCCESS);
          loadAirlines();
        } else {
          ShowToast(res?.message || "Failed to update status", Severty.ERROR);
        }
      },
      onError: (err) => {
        setLoading(false);
        const msg = err?.response?.data?.message || "Error updating status";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  // Filtering for local search
  const filteredAirlines = airlines.filter((item) => {
    const term = searchText.toLowerCase();
    return (
      (item.name && item.name.toLowerCase().includes(term)) ||
      (item.email && item.email.toLowerCase().includes(term))
    );
  });

  const columns = [
    {
      title: <Text strong>Airline Name</Text>,
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong style={{ color: "#1e3c72" }}>{text || "-"}</Text>,
    },
    {
      title: <Text strong>Email Address</Text>,
      dataIndex: "email",
      key: "email",
      render: (text) => <Text copyable>{text || "-"}</Text>,
    },
    {
      title: <Text strong>Status</Text>,
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status, record) => (
        <Tag 
          color={status === 1 ? "green" : "volcano"} 
          style={{ fontWeight: 600, cursor: "pointer" }}
          onClick={() => handleUpdateStatus(record)}
          title="Click to toggle status"
        >
          {status === 1 ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: <Text strong>Date Registered</Text>,
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (
        <Text type="secondary" style={{ fontFamily: "monospace", fontSize: "12px" }}>
          {date ? new Date(date).toLocaleDateString() : "N/A"}
        </Text>
      ),
    },
  ];

  return (
    <div style={{ padding: screens.xs ? "16px" : "32px", minHeight: "100vh", background: "#f8f9fa" }}>
      <Card
        bordered={false}
        style={{
          borderRadius: 16,
          boxShadow: "0 8px 24px rgba(19, 34, 114, 0.08)",
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0, fontWeight: 700 }}>
              Airline Management
            </Title>
            <Paragraph style={{ margin: "4px 0 0", color: "#64748b" }}>
              View and manage registered airline user accounts.
            </Paragraph>
          </div>
          <Space wrap style={{ width: screens.xs ? "100%" : "auto", justifyContent: screens.xs ? "space-between" : "flex-end" }}>
            <Input
              placeholder="Search airlines..."
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: screens.xs ? "100%" : 260, borderRadius: 10, height: 42 }}
              allowClear
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAddModalVisible(true)}
              style={{
                height: 42,
                borderRadius: 10,
                fontWeight: 600,
                background: "#1e3c72",
                borderColor: "#1e3c72",
                boxShadow: "0 4px 12px rgba(30, 60, 114, 0.2)",
              }}
            >
              Add New Airline
            </Button>
          </Space>
        </div>

        <Divider style={{ margin: "20px 0" }} />

        <Table
          loading={loading}
          dataSource={filteredAirlines}
          rowKey={(record) => record._id || record.id}
          scroll={{ x: 800 }}
          columns={columns}
          pagination={{ pageSize: 10 }}
          style={{ background: "#fff", borderRadius: "12px" }}
        />
      </Card>

      {/* Add Airline User Modal */}
      <Modal
        width={screens.xs ? "95%" : 650}
        title={
          <div style={{ textAlign: "center", fontSize: 22, fontWeight: 700, color: "#1e3c72" }}>
            Add New Airline User
          </div>
        }
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          addForm.resetFields();
        }}
        centered
        footer={null}
        wrapClassName="add-airline-modal"
      >
        <Form
          form={addForm}
          layout="vertical"
          onFinish={handleCreateAirlineUser}
          requiredMark={false}
          initialValues={{ status: 1 }}
          style={{ marginTop: 20 }}
        >
          <Form.Item
            label={<Text strong>Airline User Name</Text>}
            name="name"
            rules={[{ required: true, whitespace: true, message: "Please enter the airline name" }]}
          >
            <Input size="large" placeholder="E.g., Emirates Airlines Admin" style={{ borderRadius: 10 }} />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<Text strong>Email Address</Text>}
                name="email"
                rules={[
                  { required: true, message: "Please enter the email address" },
                  { type: "email", whitespace: true, message: "Enter a valid email address" },
                ]}
              >
                <Input size="large" placeholder="E.g., contact@emirates.com" style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
               <Form.Item
                  label={<Text strong>Account Password</Text>}
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your password",
                    },
                    {
                      min: 8,
                      message: "Password must be at least 8 characters long",
                    },
                    {
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
                      message:
                        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
                    },
                  ]}
                >
                <Input.Password size="large" placeholder="Password" style={{ borderRadius: 10 }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<Text strong>Status</Text>}
            name="status"
            rules={[{ required: true, message: "Please select status" }]}
          >
            <Select size="large" style={{ borderRadius: 10 }}>
              <Select.Option value={1}>Active</Select.Option>
              <Select.Option value={0}>Inactive (Suspend)</Select.Option>
            </Select>
          </Form.Item>

          <Divider style={{ margin: "24px 0 16px" }} />

          <div style={{ textAlign: "right" }}>
            <Space>
              <Button
                size="large"
                style={{ borderRadius: 10, minWidth: 100 }}
                onClick={() => {
                  setAddModalVisible(false);
                  addForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={addLoading}
                size="large"
                style={{
                  minWidth: 120,
                  background: "#1e3c72",
                  borderColor: "#1e3c72",
                  borderRadius: 10,
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(30, 60, 114, 0.2)",
                }}
              >
                Register Airline
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      <style>{`
        .add-airline-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }
        .add-airline-modal .ant-modal-header {
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          padding: 16px 24px;
        }
        .add-airline-modal .ant-form-item-label > label {
          color: #334155;
        }
      `}</style>
    </div>
  );
};

export default AirlineModule;
